import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { clearDashboardSessions, getActiveDashboardSession, setStaffSession } from '../utils/session';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api';

export const StaffLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [hasSession, setHasSession] = useState(() => Boolean(getActiveDashboardSession()));
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (hasSession) {
      navigate('/admin/submissions', { replace: true });
    }
  }, [hasSession, navigate]);

  const clearSession = () => {
    clearDashboardSessions();
    setHasSession(false);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/staff/login`, formData);
      setStaffSession(response.data.token, response.data.user);
      setHasSession(true);
      navigate('/admin/submissions', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-white flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-slate-200 p-8">
        <div className="mb-8">
          <p className="text-sm uppercase tracking-[0.3em] text-slate-500 mb-3">Staff Access</p>
          <h1 className="text-3xl font-bold text-slate-900">Staff Login</h1>
          <p className="mt-3 text-sm text-slate-600">
            Only approved staff accounts can sign in. Pending accounts must wait for admin approval.
          </p>
        </div>

        {/* We keep this hidden visually to not clash but just in case react delays */}
        {hasSession && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900 hidden">
            <p>A dashboard session already exists in this browser.</p>
            <button
              type="button"
              onClick={clearSession}
              className="mt-3 inline-flex items-center justify-center rounded-lg bg-amber-600 px-3 py-2 text-white font-medium hover:bg-amber-700"
            >
              Clear Current Staff Session
            </button>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData((current) => ({ ...current, email: e.target.value }))}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData((current) => ({ ...current, password: e.target.value }))}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-slate-900 px-6 py-3 text-white font-medium hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-sm text-gray-600">
          Need an account?{' '}
          <Link to="/staff/signup" className="font-medium text-blue-600 hover:text-blue-700">
            Create staff account
          </Link>
        </div>
      </div>
    </div>
  );
};
